import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, CardBody, Input } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import {
  approveTypeTag,
  listPendingTypeTags,
  loadTypeTags,
  mergeTypeTag,
  rejectTypeTag,
  renameTypeTag,
  retireTypeTag,
  TYPE_TAG_GROUPS,
  TypeTag,
  TypeTagAdminRow,
  TypeTagResult,
} from "../../services/typeTagApi";

const groupLabel = (group: string) => TYPE_TAG_GROUPS.find((g) => g.group === group)?.label || group;

const isPlatformAdmin = (): boolean => {
  try {
    const role = JSON.parse(localStorage.getItem("GetUserRes") || "null")?.users?.role;
    return role === "SUPER_ADMIN" || role === "ADMIN";
  } catch {
    return false;
  }
};

/**
 * Review the shared type-tag list: any studio admin approves, rejects or merges proposals; HKP
 * admins can also rename and retire tags. The server decides who may do what; this page only
 * hides what a viewer cannot use.
 */
const TypeTags = () => {
  const platform = isPlatformAdmin();
  const [pending, setPending] = useState<TypeTagAdminRow[] | null>(null);
  const [approved, setApproved] = useState<TypeTag[]>([]);
  const [denied, setDenied] = useState("");
  const [busyId, setBusyId] = useState("");
  const [mergeTarget, setMergeTarget] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<{ id: string; nameEn: string; nameHi: string } | null>(null);

  const load = useCallback(async () => {
    const [proposals, tags] = await Promise.all([listPendingTypeTags(), loadTypeTags()]);
    if (proposals.ok) {
      setPending(proposals.data || []);
      setDenied("");
    } else {
      setPending([]);
      setDenied(proposals.status === 403 ? "Only a studio admin can review proposals." : proposals.message);
    }
    setApproved(tags || []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Runs one action and tells the caller whether it worked, so a form can stay open when it did not. */
  const act = async (id: string, work: () => Promise<TypeTagResult<unknown>>, done: string): Promise<boolean> => {
    if (busyId) return false;
    setBusyId(id);
    const result = await work();
    setBusyId("");
    notifications({
      title: result.ok ? "Done" : "Not done",
      message: result.ok ? done : result.message,
      type: result.ok ? "success" : "danger",
    });
    await load();
    return result.ok;
  };

  return (
    <div className="userMainDiv">
      <Card className="createProductCard p-3">
        <h4 className="mb-2">Type tags</h4>
        <p className="text-muted mb-4">
          The shared list of types (katan, tanchoi, butidar…) that every studio picks from. Studios propose new ones here.
        </p>
        <CardBody className="p-0">
          <h5 className="mb-3">Waiting for approval{pending && pending.length ? ` (${pending.length})` : ""}</h5>
          {pending === null ? <p role="status">Loading…</p> : null}
          {denied ? <p role="alert">{denied}</p> : null}
          {pending && !denied && pending.length === 0 ? <p className="text-muted">Nothing is waiting.</p> : null}
          {(pending || []).map((row) => {
            const sameGroup = approved.filter((t) => t.group === row.group);
            const target = mergeTarget[row.id] || "";
            return (
              <div key={row.id} className="d-flex flex-wrap align-items-center gap-2 border rounded p-2 mb-2">
                <div className="me-auto">
                  <strong>{row.nameEn}</strong>
                  {row.nameHi ? <span lang="hi"> · {row.nameHi}</span> : null}
                  <div className="text-muted small">{groupLabel(row.group)}</div>
                </div>
                <Button size="sm" color="primary" disabled={!!busyId} onClick={() => act(row.id, () => approveTypeTag(row.id), `“${row.nameEn}” is now in the list.`)}>
                  Approve
                </Button>
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={!!busyId}
                  onClick={() => act(row.id, () => rejectTypeTag(row.id), `“${row.nameEn}” was turned down.`)}
                >
                  Reject
                </Button>
                {sameGroup.length ? (
                  <span className="d-flex gap-1">
                    <Input
                      type="select"
                      bsSize="sm"
                      aria-label={`Merge ${row.nameEn} into`}
                      value={target}
                      onChange={(e: any) => setMergeTarget({ ...mergeTarget, [row.id]: e.target.value })}
                    >
                      <option value="">Same as…</option>
                      {sameGroup.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.nameEn}
                        </option>
                      ))}
                    </Input>
                    <Button
                      size="sm"
                      outline
                      disabled={!target || !!busyId}
                      onClick={() => act(row.id, () => mergeTypeTag(row.id, target), `“${row.nameEn}” was merged.`)}
                    >
                      Merge
                    </Button>
                  </span>
                ) : null}
              </div>
            );
          })}

          {platform ? (
            <>
              <h5 className="mt-4 mb-3">All tags</h5>
              {TYPE_TAG_GROUPS.map((g) => {
                const rows = approved.filter((t) => t.group === g.group);
                if (!rows.length) return null;
                return (
                  <div key={g.group} className="mb-3">
                    <div className="text-muted small mb-1">{g.label}</div>
                    {rows.map((t) =>
                      editing?.id === t.id ? (
                        <form
                          key={t.id}
                          className="d-flex flex-wrap gap-2 mb-2"
                          onSubmit={(e) => {
                            e.preventDefault();
                            act(t.id, () => renameTypeTag(t.id, { group: t.group, nameEn: editing.nameEn, nameHi: editing.nameHi || undefined }), "Tag updated.").then((ok) => {
                              if (ok) setEditing(null);
                            });
                          }}
                        >
                          <Input bsSize="sm" aria-label="Name" value={editing.nameEn} maxLength={60} onChange={(e: any) => setEditing({ ...editing, nameEn: e.target.value })} />
                          <Input bsSize="sm" aria-label="Hindi label" lang="hi" value={editing.nameHi} maxLength={60} onChange={(e: any) => setEditing({ ...editing, nameHi: e.target.value })} />
                          <Button size="sm" color="primary" type="submit" disabled={!!busyId || !editing.nameEn.trim()}>
                            Save
                          </Button>
                          <Button size="sm" outline type="button" onClick={() => setEditing(null)}>
                            Cancel
                          </Button>
                        </form>
                      ) : (
                        <div key={t.id} className="d-flex align-items-center gap-2 mb-1">
                          <span className="me-auto">
                            {t.nameEn}
                            {t.nameHi ? <span lang="hi"> · {t.nameHi}</span> : null}
                          </span>
                          <Button size="sm" outline onClick={() => setEditing({ id: t.id, nameEn: t.nameEn, nameHi: t.nameHi || "" })}>
                            Rename
                          </Button>
                          <Button
                            size="sm"
                            outline
                            color="danger"
                            disabled={!!busyId}
                            onClick={() => {
                              if (window.confirm(`Retire “${t.nameEn}”? Products keep it, but nobody can pick it again.`)) {
                                act(t.id, () => retireTypeTag(t.id), `“${t.nameEn}” was retired.`);
                              }
                            }}
                          >
                            Retire
                          </Button>
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
};

export default TypeTags;
